import { BrowserDetectService } from '$core/services/browser-detect.service';
import { EyeSightService } from '$core/services/eye-sight.service';
import { AnimationPlayer } from '@angular/animations';
import {
  AfterViewInit,
  Directive,
  ElementRef,
  HostBinding,
  OnDestroy,
  OnInit,
  Renderer2,
} from '@angular/core';
import { fromEvent } from 'rxjs';
import {
  distinctUntilChanged,
  filter,
  map,
  pairwise,
  share,
  tap,
} from 'rxjs/operators';

enum Direction {
  Up = 'Up',
  Down = 'Down',
}

@Directive({
  selector: '[stickyHeader]',
})
export class StickyHeaderDirective implements OnInit, AfterViewInit, OnDestroy {
  public lastY = 0;
  public threshold = 50;
  public player: AnimationPlayer;

  @HostBinding('class.show')
  public get isShow() {
    return this.show;
  }

  @HostBinding('class.hidden')
  public get isHidden() {
    return !this.show;
  }

  @HostBinding('class.scrolled')
  public get isScrolled() {
    if (this.eye.enabled) {
      return false;
    }

    return this.lastY >= this.threshold;
  }

  private _show = true;
  public get show() {
    return this._show;
  }
  public set show(show: boolean) {
    this._show = show;
  }

  constructor(
    private elRef: ElementRef,
    private renderer: Renderer2,
    private eye: EyeSightService,
    private browserDetectService: BrowserDetectService
  ) {}

  public ngOnInit() {
    if (!this.browserDetectService.isIE) {
      this.eye.status$.subscribe((e) => {
        if (e) {
          this.removeFixedFromEl();
        }
      });
    }
  }

  public ngAfterViewInit() {
    if (this.browserDetectService.isIE) {
      return;
    }

    const scroll$ = fromEvent(window, 'scroll', { passive: true }).pipe(
      map(() => window.pageYOffset),
      tap((y) => (this.lastY = y)),
      pairwise(),
      map(([prev, curr]): Direction => {
        if (
          window.innerHeight + window.pageYOffset >=
            document.body.scrollHeight ||
          this.eye.enabled
        ) {
          return null;
        }

        if (curr < 0) {
          this.removeFixedFromEl();
        } else {
          this.setFixedForEl();
        }

        if (curr >= this.threshold) {
          if (curr < prev || prev < 0) {
            if (prev - curr > 50) {
              return Direction.Up;
            }
          } else {
            return Direction.Down;
          }
        }

        return null;
      }),
      distinctUntilChanged(),
      share()
    );

    const goingUp$ = scroll$.pipe(
      filter((direction) => direction === Direction.Up)
    );

    const goingDown$ = scroll$.pipe(
      filter((direction) => direction === Direction.Down)
    );

    goingUp$.subscribe(() => (this.show = true));
    goingDown$.subscribe(() => (this.show = false));
  }

  private removeFixedFromEl() {
    this.renderer.setStyle(this.elRef.nativeElement, 'position', '');
  }

  private setFixedForEl() {
    this.renderer.setStyle(this.elRef.nativeElement, 'position', 'fixed');
  }

  public ngOnDestroy() {}
}
